import React from 'react';
import { ShieldOffIcon, ShieldXIcon } from 'lucide-react';
import type { Monitor } from '../types/monitor';
import { Panel } from './ui/Panel';
import { PanelHeader } from './ui/PanelHeader';
import { StatusBadge } from './StatusBadge';
import { certificateDaysRemaining, sslStatus } from '../utils/health';
import { formatDate } from '../utils/format';
const dayTone = {
  healthy: 'text-healthy-ink',
  warning: 'text-warn-ink',
  critical: 'text-critical-ink',
  paused: 'text-paused-ink'
} as const;
function DetailRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-start justify-between gap-4 border-b border-line py-2.5 last:border-b-0" data-id="element-243" data-mp="2c6w1x7b42l8i">
      <dt className="text-[13px] text-ink-muted" data-id="element-244" data-mp="2h68n7nmc3k8z">{label}</dt>
      <dd className="max-w-[62%] break-words text-right text-[13px] font-medium text-ink" data-id="element-245" data-mp="2h3mfnnzg14qr">
        {value}
      </dd>
    </div>;
}
export function CertificateCard({
  monitor
}: {
  monitor: Monitor;
}) {
  const status = sslStatus(monitor);
  const days = certificateDaysRemaining(monitor);
  return <Panel as="section" data-id="element-246" data-mp="hh4bydbd591w">
      <PanelHeader title="TLS certificate" description={monitor.sslEnabled ? `Warning when fewer than ${monitor.warningWindowDays} days remain.` : undefined} action={<StatusBadge status={status} size="md" data-id="element-248" data-mp="3k8t5t5b838n" />} data-id="element-247" data-mp="ckdlqx5ugx2r" />

      {!monitor.sslEnabled && <div className="flex items-start gap-3 px-5 py-6" data-id="element-249" data-mp="3uq0e8e48cpol">
          <ShieldOffIcon className="mt-0.5 h-5 w-5 shrink-0 text-paused" aria-hidden="true" data-id="element-250" data-mp="3url9k5ydccni" />
          <p className="text-sm text-ink-muted" data-id="element-251" data-mp="3kzfhyxc13x8u">
            The certificate check is switched off for this domain, so nothing is
            being inspected and no expiry warnings will be sent. Switch it on
            below to start watching it.
          </p>
        </div>}

      {monitor.sslEnabled && !monitor.certificate && <div className="flex items-start gap-3 px-5 py-6" data-id="element-252" data-mp="370o85n478xqi">
          <ShieldXIcon className="mt-0.5 h-5 w-5 shrink-0 text-critical" aria-hidden="true" data-id="element-253" data-mp="2xc0taan0urxp" />
          <div data-id="element-254" data-mp="32ceqexn34hgy">
            <p className="text-sm font-medium text-ink" data-id="element-255" data-mp="32cy8qfgnd7vi">
              We could not read a certificate
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-256" data-mp="2ngqppigz6cht">
              {monitor.certificateError ?? 'The last inspection returned no certificate.'}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted" data-id="element-257" data-mp="3g5e3yu3b41lv">
              We will keep retrying on the daily sweep. Until a certificate can
              be read, treat this domain as unprotected.
            </p>
          </div>
        </div>}

      {monitor.sslEnabled && monitor.certificate && <div className="px-5 py-5" data-id="element-258" data-mp="2wrvkrylvca5">
          <div className="flex items-end gap-3" data-id="element-259" data-mp="2vq6hjkr8p9f">
            <span className={`tabular text-4xl font-semibold leading-none ${dayTone[status]}`} data-id="element-260" data-mp="7n87g7ms2pue">
              {days}
            </span>
            <span className="pb-1 text-sm text-ink-muted" data-id="element-261" data-mp="1sc2ksvgya52y">
              days until expiry
            </span>
          </div>
          <p className="mt-2 text-[13px] text-ink-muted" data-id="element-262" data-mp="1ncpxk9m62vzu">
            Expires {formatDate(monitor.certificate.validTo)} · issued{' '}
            {formatDate(monitor.certificate.validFrom)}
          </p>

          <dl className="mt-5" data-id="element-263" data-mp="1x6q0c4tz7p5e">
            <DetailRow label="Subject" value={monitor.certificate.subject} data-id="element-264" data-mp="19dzorwpzj7vn" />
            <DetailRow label="Issuer" value={monitor.certificate.issuer} data-id="element-265" data-mp="14foi1uuzldhn" />
            <DetailRow label="Valid from" value={formatDate(monitor.certificate.validFrom)} data-id="element-266" data-mp="14hsuj16ybktj" />
            <DetailRow label="Valid to" value={formatDate(monitor.certificate.validTo)} data-id="element-267" data-mp="1edcmjcie9zd0" />
            <DetailRow label="Serial" value={monitor.certificate.serial} data-id="element-268" data-mp="2ttbf3ffj3ms5" />
            <DetailRow label="Key algorithm" value={monitor.certificate.algorithm} data-id="element-269" data-mp="2ptkhosvhy8en" />
            <DetailRow label="Chain" value={monitor.certificate.chainValid ? 'Complete and trusted' : 'Incomplete — intermediate missing'} data-id="element-270" data-mp="3l4iwyd6goa9g" />
            <DetailRow label="Alternative names" value={monitor.certificate.altNames.join(', ')} data-id="element-271" data-mp="2xl1gymgfdxk" />
          </dl>
        </div>}
    </Panel>;
}